import React, { useState } from 'react';
import { 
  Chip, 
  IconButton, 
  InputAdornment, 
  Box, 
  Typography, 
  Fade,
  Tooltip,
  Paper,
} from '@mui/material';
import { AddOutlined, DeleteOutline } from '@mui/icons-material';

/**
 * Subcomponent: Handles the list of keywords & the "Add keyword" input.
 */
const KeywordsSection = ({
  isEditing,
  keywords,
  setKeywords,
  newKeyword,
  setNewKeyword,
  handleAddKeyword,
  handleKeyDown,
  StyledTextField,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Styles
  const styles = {
    container: {
      position: 'relative',
      transition: 'all 0.3s ease',
      textAlign: 'center',
    },
    title: {
      fontSize: { xs: '0.95rem', sm: '1.1rem' },
      fontWeight: 700,
      color: '#1976d2',
      marginBottom: { xs: '10px', sm: '16px' },
      textAlign: 'center',
      letterSpacing: '0.5px',
    },
    chipsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: { xs: '6px', sm: '10px' },
      marginBottom: isEditing ? '20px' : 0,
      minHeight: '40px',
      transition: 'all 0.3s ease',
      justifyContent: 'center',
      alignItems: 'center',
    },
    chip: {
      backgroundColor: '#e3f2fd',
      color: '#1976d2',
      fontWeight: 600,
      fontSize: { xs: '0.75rem', sm: '0.875rem' },
      height: { xs: '30px', sm: '36px' },
      borderRadius: '18px',
      boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: '#bbdefb',
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)',
      },
      '& .MuiChip-deleteIcon': {
        color: '#1976d2',
        fontSize: '18px',
        transition: 'all 0.2s ease',
        '&:hover': {
          color: '#d32f2f',
          transform: 'scale(1.1)',
        },
      },
    },
    inputContainer: {
      display: 'flex',
      justifyContent: 'center',
      gap: '12px',
      marginTop: '20px',
      position: 'relative',
    },
    addButton: {
      backgroundColor: '#1976d2',
      color: 'white',
      borderRadius: '50%',
      width: '36px',
      height: '36px',
      '&:hover': {
        backgroundColor: '#1565c0',
        transform: 'scale(1.05)',
      },
    },
    inputField: {
      '& .MuiOutlinedInput-root': {
        borderRadius: '20px',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        '&:hover fieldset': {
          borderColor: '#1976d2',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#1976d2',
          borderWidth: '2px',
        },
      },
      '& .MuiInputLabel-root': {
        fontWeight: 600,
        '&.Mui-focused': {
          color: '#1976d2',
        },
      },
    },
    emptyState: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '50px',
      color: '#888',
      fontSize: '0.95rem',
      fontStyle: 'italic',
      fontWeight: 500,
    },
    tooltip: {
      backgroundColor: 'white',
      color: 'rgba(0, 0, 0, 0.87)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      borderRadius: '8px',
      '& .MuiTooltip-arrow': {
        color: 'white',
      },
    },
  };

  const handleDeleteKeyword = (keywordToDelete) => {
    setKeywords(keywords.filter((k) => k !== keywordToDelete));
  };

  return (
    <Box 
      sx={styles.container}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Typography sx={styles.title}>Keywords</Typography>
      
      <Box sx={styles.chipsContainer}>
        {keywords.length > 0 ? (
          keywords.map((keyword, index) => (
            <Fade in key={index} timeout={300}>
              <Chip
                label={keyword}
                onDelete={isEditing ? () => handleDeleteKeyword(keyword) : undefined}
                sx={styles.chip}
                size="medium"
              />
            </Fade>
          ))
        ) : (
          isEditing && (
            <Box sx={styles.emptyState}>
              Add keywords to describe your space
            </Box>
          )
        )}
      </Box>

      {isEditing && (
        <Fade in timeout={300}>
          <Box sx={styles.inputContainer}>
            <StyledTextField
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Add keyword"
              size="small"
              sx={{ ...styles.inputField, maxWidth: '220px' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip 
                      title="Add keyword" 
                      arrow
                      PopperProps={{
                        sx: styles.tooltip,
                      }}
                    >
                      <IconButton 
                        onClick={handleAddKeyword} 
                        edge="end" 
                        size="small"
                        sx={styles.addButton}
                      >
                        <AddOutlined />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default KeywordsSection;
